import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { GetBeeNumberAuthenicatedUser } from '../stores/SettingsStore'
import { 
  PwsProfileBusinessOptInGetList, 
  PwsProfileBusinessOptInSave,
  ShareCddCodeGetList
} from '@/api/AccountInfo'
import type { IMarketingOptInItem, IShareCddCodeGetListResponseItem } from '../types/AccountInfoTypes'

// Extended interface for UI display with computed properties
interface IMarketingOptInDisplay extends IMarketingOptInItem {
  displayName: string
  isOptedIn: boolean
  category: string
  hasExistingData: boolean
}

export function useMarketingOptIn() {
  const { t } = useI18n()
  const logger = useLogger()

  // State
  const optInItems = ref<IMarketingOptInDisplay[]>([])
  const originalOptInItems = ref<IMarketingOptInDisplay[]>([])
  const isLoading = ref<boolean>(false)
  const isSaving = ref<boolean>(false)
  const error = ref<string>('')
  const successMessage = ref<string>('')

  // Load marketing opt-in preferences
  async function loadOptInPreferences() {
    try {
      isLoading.value = true
      error.value = ''
      successMessage.value = ''
      
      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        logger.error('No authenticated user bee number found for marketing opt-in')
        error.value = t('accountInfo.marketingOptIn.errorNoUser')
        return
      }

      logger.debug('Loading marketing opt-in preferences for bee number:', beeNumber)
      
      // Get all available campaign types
      const availableCampaignTypes = await ShareCddCodeGetList({ CategoryId: 'CAMPAIGNTYPE' })
      logger.debug('Available campaign types:', availableCampaignTypes)
      
      // Get user's current opt-in preferences
      const userOptInResponse = await PwsProfileBusinessOptInGetList({ beeNumber })
      
      // Create a map of user's current preferences for quick lookup
      const userOptInMap = new Map<string, boolean>()
      if (userOptInResponse?.items) {
        userOptInResponse.items.forEach(item => {
          userOptInMap.set(item.codeIdCampaignType.codeId, item.codeIdOptin.codeId === 'IN')
        })
      }
      
      // Transform available campaign types to display format, merging with user preferences
      const transformedItems = availableCampaignTypes.codes.map((campaignType: IShareCddCodeGetListResponseItem) => {
        const hasExistingData = userOptInMap.has(campaignType.codeId)
        const isOptedIn = userOptInMap.get(campaignType.codeId) || false
        
        // Find the original user data to get the actual lastModifiedDate
        const originalUserData = userOptInResponse?.items?.find(item => 
          item.codeIdCampaignType.codeId === campaignType.codeId
        )
        
        return {
          codeIdCampaignType: {
            codeId: campaignType.codeId,
            name: campaignType.name,
            localizedValue: campaignType.localizedValue,
            categoryName: campaignType.categoryName,
            categoryId: campaignType.categoryId
          },
          codeIdOptin: {
            codeId: isOptedIn ? 'IN' : 'OUT',
            name: isOptedIn ? 'Opted In' : 'Opted Out',
            localizedValue: isOptedIn ? 'Opted In' : 'Opted Out',
            categoryName: 'OptIn',
            categoryId: 'OPTIN'
          },
          lastModifiedDate: originalUserData?.lastModifiedDate || new Date().toISOString(),
          displayName: campaignType.localizedValue || campaignType.name,
          isOptedIn: isOptedIn,
          category: campaignType.categoryName,
          hasExistingData: hasExistingData
        }
      })
      
      optInItems.value = transformedItems
      // Store deep copy of original values for change tracking
      originalOptInItems.value = JSON.parse(JSON.stringify(transformedItems))
      logger.debug('Marketing opt-in preferences loaded:', optInItems.value.length, 'items')
      
    } catch (err) {
      console.error('Error loading marketing opt-in preferences:', err)
      error.value = t('accountInfo.marketingOptIn.errorLoading')
    } finally {
      isLoading.value = false
    }
  }

  // Save marketing opt-in preferences
  async function saveOptInPreferences() {
    try {
      isSaving.value = true
      error.value = ''
      successMessage.value = ''
      
      const beeNumber = await GetBeeNumberAuthenicatedUser()
      if (!beeNumber) {
        logger.error('No authenticated user bee number found for saving marketing opt-in')
        error.value = t('accountInfo.marketingOptIn.errorNoUser')
        return
      }

      // Find items that have changed
      const changedItems = optInItems.value.filter(item => {
        const original = originalOptInItems.value.find(orig => 
          orig.codeIdCampaignType.codeId === item.codeIdCampaignType.codeId
        )
        return original && original.isOptedIn !== item.isOptedIn
      })

      if (changedItems.length === 0) {
        successMessage.value = t('accountInfo.marketingOptIn.noChangesToSave')
        logger.debug('No changes detected, skipping save operation')
        return
      }

      logger.debug('Found', changedItems.length, 'changed items to save')

      // Save only changed opt-in preferences
      const savePromises = changedItems.map(async (item) => {
        const request = {
          BeeNumber: beeNumber,
          CodeIdCampaignType: item.codeIdCampaignType.codeId,
          CodeIdOptIn: item.isOptedIn ? 'IN' : 'OUT'
        }
        
        logger.debug('Saving changed opt-in preference:', request)
        return await PwsProfileBusinessOptInSave(request)
      })

      // Wait for all saves to complete
      const responses = await Promise.all(savePromises)
      
      // Check if all saves were successful (empty response or success=true indicates success)
      const failedSaves = responses.filter(response => {
        // If response is empty/null, consider it successful
        if (!response) return false
        // If response has success property, check it
        if (response.hasOwnProperty('success')) return !response.success
        // If response has responseStatus with errors, consider it failed
        if (response.responseStatus?.errors && response.responseStatus.errors.length > 0) return true
        // Otherwise consider it successful
        return false
      })
      
      if (failedSaves.length === 0) {
        successMessage.value = t('accountInfo.marketingOptIn.saveSuccess')
        // Update original values to reflect saved state
        originalOptInItems.value = JSON.parse(JSON.stringify(optInItems.value))
        logger.debug('All changed marketing opt-in preferences saved successfully')
      } else {
        error.value = t('accountInfo.marketingOptIn.errorSaving')
        logger.error('Some marketing opt-in preferences failed to save:', failedSaves)
      }
    } catch (err) {
      console.error('Error saving marketing opt-in preferences:', err)
      error.value = t('accountInfo.marketingOptIn.errorSaving')
    } finally {
      isSaving.value = false
    }
  }

  // Toggle opt-in status for a specific item
  function toggleOptIn(campaignTypeCodeId: string) {
    const item = optInItems.value.find(item => item.codeIdCampaignType.codeId === campaignTypeCodeId)
    if (item) {
      item.isOptedIn = !item.isOptedIn
      // Update the underlying codeIdOptin to reflect the change
      item.codeIdOptin.codeId = item.isOptedIn ? 'IN' : 'OUT'
      item.codeIdOptin.name = item.isOptedIn ? 'In' : 'Out'
      item.codeIdOptin.localizedValue = item.isOptedIn ? 'In' : 'Out'
      logger.debug('Toggled opt-in status for:', item.displayName, 'New status:', item.isOptedIn)
    }
  }

  // Check if there are any changes to save
  function hasUnsavedChanges(): boolean {
    return optInItems.value.some(item => {
      const original = originalOptInItems.value.find(orig => 
        orig.codeIdCampaignType.codeId === item.codeIdCampaignType.codeId
      )
      return original && original.isOptedIn !== item.isOptedIn
    })
  }

  // Clear success message
  function clearSuccessMessage() {
    successMessage.value = ''
  }

  return {
    // State
    optInItems,
    isLoading,
    isSaving,
    error,
    successMessage,
    
    // Methods
    loadOptInPreferences,
    saveOptInPreferences,
    toggleOptIn,
    hasUnsavedChanges,
    clearSuccessMessage
  }
}
